import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import "./App.css";
import { AuthProvider, useAuth, homeForRole } from "./lib/auth";
import AppLayout from "./components/AppLayout";
import { DisclaimerBanner } from "./components/UI";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import ManagerDashboard from "./pages/ManagerDashboard";
import HRDashboard from "./pages/HRDashboard";
import CompanyAdmin from "./pages/CompanyAdmin";
import SuperDashboard from "./pages/SuperDashboard";
import CaseList from "./pages/CaseList";
import CreateCase from "./pages/CreateCase";
import CaseDetail from "./pages/CaseDetail";
import UsersManager from "./pages/UsersManager";
import Policies from "./pages/Policies";
import PolicyDetail from "./pages/PolicyDetail";
import Resources from "./pages/Resources";
import PoliciesAuthoring from "./pages/PoliciesAuthoring";
import MonitoringDashboard from "./pages/MonitoringDashboard";
import AuditLog from "./pages/AuditLog";
import Tenants from "./pages/Tenants";
import EnterpriseIntelligence from "./pages/EnterpriseIntelligence";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import Approvals from "./pages/Approvals";
import Workflows from "./pages/Workflows";
import CompanyProfile from "./pages/CompanyProfile";
import Billing from "./pages/Billing";
import Tickets from "./pages/Tickets";
import TicketDetail from "./pages/TicketDetail";
import HelpDocs from "./pages/HelpDocs";
import SettingsNotifications from "./pages/SettingsNotifications";
import SettingsPrivacy from "./pages/SettingsPrivacy";
import SettingsPreferences from "./pages/SettingsPreferences";
import SettingsIntegrations from "./pages/SettingsIntegrations";
import Intake from "./pages/Intake";
import IntakeStatus from "./pages/IntakeStatus";
import EmployeesImport from "./pages/EmployeesImport";

function Protected({ children, allow }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-16 text-center text-slate-500 font-mono text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (allow && !allow.includes(user.role)) return <Navigate to={homeForRole(user.role)} replace />;
  return (
    <AppLayout>
      <DisclaimerBanner />
      {children}
    </AppLayout>
  );
}

function RoleHome() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeForRole(user.role)} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/home" element={<RoleHome />} />

      {/* Sprint 1 , External Web Intake Form (unauthenticated) */}
      <Route path="/intake" element={<Intake />} />
      <Route path="/intake/status" element={<IntakeStatus />} />
      <Route path="/intake/:tenantSlug" element={<Intake />} />
      <Route path="/intake/:tenantSlug/status" element={<IntakeStatus />} />

      {/* Employee */}
      <Route path="/dashboard" element={<Protected allow={["employee","manager"]}><EmployeeDashboard /></Protected>} />

      {/* Manager */}
      <Route path="/manager" element={<Protected allow={["manager"]}><ManagerDashboard /></Protected>} />

      {/* HR */}
      <Route path="/hr" element={<Protected allow={["hr"]}><HRDashboard /></Protected>} />
      <Route path="/hr/policies" element={<Navigate to="/policies" replace />} />
      <Route path="/hr/resources" element={<Protected allow={["hr"]}><Resources /></Protected>} />
      <Route path="/hr/resources/policies" element={<Protected allow={["hr"]}><PoliciesAuthoring /></Protected>} />
      <Route path="/hr/audit" element={<Protected allow={["hr"]}><AuditLog /></Protected>} />

      {/* Company Admin */}
      <Route path="/admin" element={<Protected allow={["company_admin"]}><CompanyAdmin /></Protected>} />
      <Route path="/admin/users" element={<Protected allow={["company_admin"]}><UsersManager /></Protected>} />
      <Route path="/admin/users/import" element={<Protected allow={["company_admin","super_admin"]}><EmployeesImport /></Protected>} />
      <Route path="/admin/policies" element={<Navigate to="/policies" replace />} />
      <Route path="/admin/resources" element={<Protected allow={["company_admin"]}><Resources /></Protected>} />
      <Route path="/admin/resources/policies" element={<Protected allow={["company_admin"]}><PoliciesAuthoring /></Protected>} />
      <Route path="/admin/audit" element={<Protected allow={["company_admin"]}><AuditLog /></Protected>} />
      <Route path="/admin/enterprise-intelligence" element={<Protected allow={["company_admin"]}><EnterpriseIntelligence /></Protected>} />
      <Route path="/admin/approvals" element={<Protected allow={["company_admin"]}><Approvals /></Protected>} />
      <Route path="/admin/workflows" element={<Protected allow={["company_admin","manager"]}><Workflows /></Protected>} />
      <Route path="/admin/company-profile" element={<Protected allow={["company_admin"]}><CompanyProfile /></Protected>} />
      <Route path="/admin/billing" element={<Protected allow={["company_admin"]}><Billing /></Protected>} />

      {/* Super Admin */}
      <Route path="/super" element={<Protected allow={["super_admin"]}><SuperDashboard /></Protected>} />
      <Route path="/super/tenants" element={<Protected allow={["super_admin"]}><Tenants /></Protected>} />
      <Route path="/super/audit" element={<Protected allow={["super_admin"]}><AuditLog /></Protected>} />
      <Route path="/super/enterprise-intelligence" element={<Protected allow={["super_admin"]}><EnterpriseIntelligence /></Protected>} />
      <Route path="/super/workflows" element={<Protected allow={["super_admin"]}><Workflows /></Protected>} />

      {/* Support Tickets (all authenticated) */}
      <Route path="/tickets" element={<Protected><Tickets /></Protected>} />
      <Route path="/tickets/:id" element={<Protected><TicketDetail /></Protected>} />

      {/* Profile & Settings (all authenticated) */}
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/settings/notifications" element={<Protected><SettingsNotifications /></Protected>} />
      <Route path="/settings/privacy" element={<Protected><SettingsPrivacy /></Protected>} />
      <Route path="/settings/preferences" element={<Protected><SettingsPreferences /></Protected>} />
      <Route path="/settings/integrations" element={<Protected><SettingsIntegrations /></Protected>} />
      <Route path="/security" element={<Protected><Profile /></Protected>} />
      <Route path="/help" element={<Protected><HelpDocs /></Protected>} />

      {/* Policies signing library (all roles) + Organization Resources authoring */}
      <Route path="/policies" element={<Protected><Policies /></Protected>} />
      <Route path="/policies/:id" element={<Protected><PolicyDetail /></Protected>} />
      <Route path="/resources" element={<Protected allow={["hr", "company_admin", "super_admin"]}><Resources /></Protected>} />
      <Route path="/resources/policies" element={<Protected allow={["hr", "company_admin", "super_admin"]}><PoliciesAuthoring /></Protected>} />
      <Route path="/resources/:id" element={<Protected><PolicyDetail /></Protected>} />

      {/* Corrective-action monitoring dashboard */}
      <Route path="/monitoring" element={<Protected allow={["hr", "company_admin", "super_admin", "manager"]}><MonitoringDashboard /></Protected>} />

      {/* Cases (shared) */}
      <Route path="/cases" element={<Protected><CaseList /></Protected>} />
      <Route path="/cases/new" element={<Protected allow={["employee","manager","hr","company_admin"]}><CreateCase /></Protected>} />
      <Route path="/cases/:id" element={<Protected><CaseDetail /></Protected>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="bottom-right" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
